import React, { useMemo, useState } from 'react';

import { I18n } from '@aws-amplify/core';
import { WarningOutlined, ExpandAltOutlined } from '@ant-design/icons';

import { POSTURE_OPTIONS } from '@/components/views/Report/KimLhc/Report/Steps/Posture/Fields/BodyPostureOptions';
import * as PostureStyles from '@/components/views/Report/KimLhc/Components/Shared/PostureDisplay/styles';
import { PostureDisplayCard, PostureDisplayModal } from '@/components/views/Report/KimLhc/Components/Shared/PostureDisplay';
import {
	POSTURE_TWIST_LABELS,
	POSTURE_LOAD_CENTER_LABELS,
	POSTURE_ARMS_LABELS,
	POSTURE_HANDS_LABELS
} from '@/components/views/Report/KimLhc/hooks/question-labels';
import { getWarningIconColor } from '../../../../utils/helper';
import { KimLhcCategoryKey, type PostureCategory } from '@/types';

import * as S from '@/components/views/Report/KimLhc/Report/Steps/Result/styles';

interface PostureCategoryProps {
	data: PostureCategory;
}

type AdditionalFactor = { key: string; labelKey: string };

function toAdditionalFactors(additionalFactors: PostureCategory['additionalFactors']): AdditionalFactor[] {
	return [
		{ key: additionalFactors.twist, labelKey: POSTURE_TWIST_LABELS[additionalFactors.twist!] },
		{ key: additionalFactors.loadCenter, labelKey: POSTURE_LOAD_CENTER_LABELS[additionalFactors.loadCenter!] },
		{ key: additionalFactors.arms, labelKey: POSTURE_ARMS_LABELS[additionalFactors.arms!] },
		{ key: additionalFactors.hands, labelKey: POSTURE_HANDS_LABELS[additionalFactors.hands!] },
	].filter(({ key }) => Boolean(key)) as AdditionalFactor[];
}

function AdditionalFactorsList({ factors }: Readonly<{ factors: AdditionalFactor[] }>) {
	if (factors.length === 0) {
		return <S.EmptyText>{I18n.get('No additional posture factors reported')}</S.EmptyText>;
	}

	function getWarningIconColorByFactorKey(key: string): string {
		return getWarningIconColor(KimLhcCategoryKey.POSTURE, key);
	}

	return (
		<S.FactorsList>
			{factors.map((factor) => (
				<S.FactorItem key={factor.key}>
					<S.WarningIcon>
						<WarningOutlined style={{ color: getWarningIconColorByFactorKey(factor.key) }} />
					</S.WarningIcon>
					<S.FactorText>{I18n.get(factor.labelKey)}</S.FactorText>
				</S.FactorItem>
			))}
		</S.FactorsList>
	);
}

export function PostureCategory({ data }: Readonly<PostureCategoryProps>): JSX.Element {
	const [modalOpen, setModalOpen] = useState(false);

	const postureOption = useMemo(
		() => POSTURE_OPTIONS.find((o) => o.id === data.value),
		[data.value]
	);

	const additionalFactors = useMemo(
		() => toAdditionalFactors(data.additionalFactors),
		[data.additionalFactors]
	);

	const handleOpenModal = () => setModalOpen(true);
	const handleCloseModal = () => setModalOpen(false);

	return (
		<S.PostureColumns>
			<S.PostureColumn>
				<S.ContentSectionTitle>{I18n.get('Posture')}</S.ContentSectionTitle>
				{postureOption && (
					<S.PostureCardWrapper>
						<PostureDisplayCard
							startLabel={postureOption.startLabel}
							endLabel={postureOption.endLabel}
							startImages={postureOption.startImages}
							endImages={postureOption.endImages}
						/>
						<PostureStyles.ExpandButton
							type="text"
							icon={<ExpandAltOutlined />}
							onClick={handleOpenModal}
						/>
					</S.PostureCardWrapper>
				)}
			</S.PostureColumn>

			<S.PostureColumn>
				<S.ContentSectionTitle>{I18n.get('Additional factors')}</S.ContentSectionTitle>
				<AdditionalFactorsList factors={additionalFactors} />
			</S.PostureColumn>

			{postureOption && (
				<PostureStyles.StyledModal
					open={modalOpen}
					onCancel={handleCloseModal}
					footer={null}
					width={700}
					centered
					title={I18n.get('Body posture')}
				>
					<PostureStyles.ModalContent>
						<PostureDisplayModal
							startLabel={postureOption.startLabel}
							endLabel={postureOption.endLabel}
							startImages={postureOption.startImages}
							endImages={postureOption.endImages}
						/>
					</PostureStyles.ModalContent>
				</PostureStyles.StyledModal>
			)}
		</S.PostureColumns>
	);
}
